// src/App.function.js
//
// App.js is intentionally kept as a small route table. This companion module owns
// the behavior behind those routes: auth shell composition, profile hydration,
// staff MFA enforcement, CAPTCHA signup handling, notification wiring, and
// walkthrough state. Keeping these concerns here makes route changes easier to
// review without mixing them with side-effect-heavy application setup.
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import QRCode from "qrcode";
import {
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  getCurrentUser,
  setUpTOTP,
  signUp,
  updateMFAPreference,
  verifyTOTPSetup,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

import { client } from "./amplify-client";
import {
  AGREEMENT_POLICY_CONFIG,
  buildAgreementClientMetadata,
  clearPendingPolicyAcceptancesForEmail,
  createDefaultAgreementSelections,
  getPendingPolicyAcceptanceInputs,
  queuePendingPolicyAcceptances,
} from "./policies/agreement-service";
import { getLegalDocument } from "./policies/legal-content";
import {
  isGpcSignalActive,
  setStoredOptOut,
  syncPrivacyOptOutToProfile,
} from "./policies/privacy-preference-service";
import { pickChatColor, resolveProfileImageUrlFromValue } from "./api/profile";
import Navigation from "./components/navigation/Navigation";
import betterIntentionsLogoStandard from "./assets/better-intentions-logo-s.png";
import Active from "./screens/active/Active";
import { ActiveProvider } from "./screens/active/active.functions";
import Past from "./screens/past/Past";
import { PastProvider } from "./screens/past/past.functions";
import { PingActivityProvider } from "./screens/ping-activity/ping-activity.functions";
import { CommunityHub } from "./screens/community/Community";
import {
  getNextMonotonicIso,
  extractUserChannelPayload,
  getCurrentOpenChatPk,
  publishCommunicationEvent,
  loadCommunicationInboxSnapshot,
} from "./api/communication";
import {
  createIdleSessionController,
  IDLE_TIMEOUT_MS,
  IDLE_WARNING_MS,
  readPendingMessageIntent,
} from "./client-state/session";
import { getMyAccessState } from "./api/reporting";
import { startPresenceHeartbeat } from "./api/presence";
import {
  hasInitiatedChatRequest,
  getWalkthroughCompletion,
  markWalkthroughCompleted,
} from "./api/walkthrough";
import WalkthroughOverlay from "./components/walkthrough/walkthrough-overlay";

const STAFF_GROUPS = new Set(["superadmins", "admins", "moderators", "marketing"]);

const TURNSTILE_SIGNUP_ACTION = "web_signup";

// The first-run product tour is route-driven. Each step states where it should
// run, which element it highlights, and whether user action is required before
// advancing. Community screens communicate progress with `walkthrough:action`
// events so App.function.js remains the single source of walkthrough state.
const WALKTHROUGH_STEPS = [
  {
    id: "nav-connect",
    route: "/active",
    advanceOnPathPrefix: "/connect",
    selector: '[data-walkthrough="nav-connect"]',
    title: "Open the Connect tab",
    description: "Start in Connect to discover people and send requests.",
    requiresAction: true,
    actionLabel: "Click the highlighted Connect tab to continue.",
  },
  {
    id: "filters-overview",
    route: "/connect",
    selector: '[data-walkthrough="community-filters"]',
    actionSelector: '[data-walkthrough="apply-filters"]',
    tooltipAnchorSelector: '[data-walkthrough="apply-filters"]',
    scrollTargetSelector: '[data-walkthrough="apply-filters"]',
    title: "Use Filters To Narrow The Search",
    description: "This entire panel is where people shape who they want to meet. For the walkthrough, go straight to Apply Filters so you can see how matches appear.",
    tooltipPlacement: "right",
    tooltipWidth: 300,
    scrollBlock: "end",
    requiresAction: true,
    actionLabel: "Click Apply Filters to continue.",
  },
  {
    id: "results-overview",
    route: "/connect",
    selector: '[data-walkthrough="community-results"]',
    scrollTargetSelector: '[data-walkthrough="community-results"]',
    spotlightPadding: 0,
    title: "Your Matches Show Up Here",
    description: "After filters run, the matching people appear in this results area. This is where you can browse profiles and decide who to contact.",
  },
  {
    id: "send-request",
    route: "/connect",
    selector: '[data-walkthrough="community-results"]',
    actionSelector: '[data-walkthrough="send-request"]',
    scrollTargetSelector: '[data-walkthrough="send-request"]',
    title: "Start A Conversation Request",
    description: "When someone looks interesting, this is the step that opens a conversation request. Use the highlighted Send Request button to preview the flow.",
    requiresAction: true,
    actionLabel: "Click the highlighted Send Request button to continue.",
  },
  {
    id: "request-message",
    route: "/connect",
    selector: '[data-walkthrough="request-composer"]',
    scrollTargetSelector: '[data-walkthrough="request-message"]',
    title: "Review The Request Composer",
    description: "This composer is where a request message would normally be written. For the walkthrough, just review the layout and continue to the final step.",
  },
  {
    id: "request-send",
    route: "/connect",
    selector: '[data-walkthrough="request-send"]',
    scrollTargetSelector: '[data-walkthrough="request-send"]',
    title: "Review Before Sending",
    description: "This is the final send step. In real use, pressing this button delivers the request. For the walkthrough, finish here without sending anything.",
    primaryLabel: "Finish Tour",
  },
];

function isLocalHostname(hostname) {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

function resolveTurnstileSiteKey() {
  if (typeof window !== "undefined" && isLocalHostname(window.location.hostname)) {
    const enableLocalCaptcha = String(process.env.REACT_APP_TURNSTILE_ENABLE_LOCAL || "")
      .trim()
      .toLowerCase();
    if (!["1", "true", "yes", "on"].includes(enableLocalCaptcha)) {
      return "";
    }
  }

  const configuredKey =
    process.env.REACT_APP_TURNSTILE_SITE_KEY_WEB || process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const trimmedConfiguredKey = String(configuredKey || "").trim();
  if (trimmedConfiguredKey) return trimmedConfiguredKey;
  return "";
}


function TurnstileSignupCaptcha({ errorMessage, onTokenChange }) {
  const [localErrorMessage, setLocalErrorMessage] = useState("");
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const siteKey = useMemo(() => resolveTurnstileSiteKey(), []);

  const applyWidgetScale = useCallback(() => {
    const container = containerRef.current;
    const row = container?.parentElement;
    if (!row) return;

    const availableWidth = Number(row.clientWidth || 0);
    const baseWidth = 300;
    const scale = availableWidth > 0 ? Math.min(1, availableWidth / baseWidth) : 1;

    row.style.setProperty("--turnstile-scale", String(scale));
  }, []);

  useEffect(() => {
    if (!siteKey) {
      setIsWidgetReady(false);
      setLocalErrorMessage("CAPTCHA is not configured for this environment.");
      onTokenChange("");
      return;
    }

    let disposed = false;

    const onToken = (token) => {
      if (disposed) return;
      if (!token) {
        setLocalErrorMessage("CAPTCHA verification failed. Please try again.");
        onTokenChange("");
        return;
      }

      setLocalErrorMessage("");
      onTokenChange(token);
    };

    const onExpire = () => {
      if (disposed) return;
      setLocalErrorMessage("Verification expired. Please complete CAPTCHA again.");
      onTokenChange("");
    };

    const onError = () => {
      if (disposed) return;
      setLocalErrorMessage("CAPTCHA verification failed. Please try again.");
      onTokenChange("");
    };

    const renderWidget = () => {
      if (disposed || !window.turnstile || !containerRef.current || widgetIdRef.current !== null) {
        return;
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action: TURNSTILE_SIGNUP_ACTION,
        size: "normal",
        callback: onToken,
        "expired-callback": onExpire,
        "error-callback": onError,
      });

      setIsWidgetReady(true);
      setLocalErrorMessage("");
      requestAnimationFrame(applyWidgetScale);
    };

    if (window.turnstile) {
      renderWidget();
      return () => {
        disposed = true;
      };
    }

    let script = document.querySelector("script[data-turnstile-signup]");
    if (!script) {
      script = document.createElement("script");
      script.setAttribute("data-turnstile-signup", "true");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.body.appendChild(script);
    } else {
      script.addEventListener("load", renderWidget);
    }

    return () => {
      disposed = true;
      if (script) {
        script.onload = null;
        script.removeEventListener("load", renderWidget);
      }
    };
  }, [applyWidgetScale, onTokenChange, siteKey]);

  useEffect(() => {
    if (!isWidgetReady) return undefined;

    const onResize = () => applyWidgetScale();
    window.addEventListener("resize", onResize, { passive: true });
    applyWidgetScale();

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [applyWidgetScale, isWidgetReady]);


  const onRetry = () => {
    if (!window.turnstile || widgetIdRef.current === null) return;
    window.turnstile.reset(widgetIdRef.current);
    requestAnimationFrame(applyWidgetScale);
    setLocalErrorMessage("");
    onTokenChange("");
  };

  return (
    <div className="biAuth__captchaWrap">
      <p className="biAuth__captchaTitle">Security verification</p>

      {localErrorMessage ? (
        <p className="biAuth__captchaError" role="alert">
          {localErrorMessage}
        </p>
      ) : null}

      {errorMessage ? (
        <p className="biAuth__captchaError" role="alert">
          {errorMessage}
        </p>
      ) : null}

      <div className="biAuth__captchaWidgetRow">
        <div ref={containerRef} className="biAuth__captchaWidget" />
      </div>

      {!isWidgetReady && !localErrorMessage ? (
        <p className="biAuth__captchaHelp">Loading CAPTCHA...</p>
      ) : (
        <button type="button" className="biAuth__captchaRetry" onClick={onRetry}>
          Retry challenge
        </button>
      )}
    </div>
  );
}

/* ----------------------------
   AUTH WRAPPER
----------------------------- */
function AuthPanelHeader({ title, subtitle }) {
  return (
    <div className="biAuth__header">
      <h1 className="biAuth__title">{title}</h1>
      <p className="biAuth__subtitle">{subtitle}</p>
    </div>
  );
}

function AuthPanelBrandHeader() {
  return (
    <Link to="/" className="biAuth__logoWrap" aria-label="Go to BetterIntentions homepage">
      <img
        src={betterIntentionsLogoStandard}
        alt="Better Intentions"
        title="Better Intentions logo"
        className="biAuth__logo"
        draggable={false}
      />
    </Link>
  );
}

function AuthPanelSignInFooter() {
  const { toForgotPassword } = useAuthenticator((context) => [context.toForgotPassword]);

  return (
    <button type="button" className="biAuth__forgotPassword" onClick={toForgotPassword}>
      Forgot password?
    </button>
  );
}

function AuthPrivacyNote() {
  return (
    <div className="biAuth__privacyNote" role="note" aria-label="Privacy note">
      <p className="biAuth__privacyNoteTitle">Privacy note</p>
      <p className="biAuth__privacyNoteBody">
        Your name and email address stay private and are not visible to community
        members or the public. If you want your name visible publicly, use it as your alias in the
        next step.
      </p>
    </div>
  );
}

function SignUpAgreementFields({ selections, onToggle }) {
  const termsPolicy = AGREEMENT_POLICY_CONFIG.find(
    (policy) => policy.policyType === "termsOfService"
  );
  const privacyPolicy = AGREEMENT_POLICY_CONFIG.find(
    (policy) => policy.policyType === "privacyPolicy"
  );
  const policiesId = "signup-agreement-required-policies";
  const consentId = "signup-agreement-sensitive-consent";

  // The Terms + Privacy acceptance is one checkbox; sensitive-data consent is a
  // SEPARATE, specific checkbox (state law requires unbundled opt-in consent
  // for sensitive data — it must not be folded into the general agreement).
  // Both are required to submit (gated by hasRequiredAgreementSelections).
  const termsAndPrivacyChecked =
    Boolean(selections?.termsOfService) && Boolean(selections?.privacyPolicy);

  return (
    <fieldset className="biAuth__agreementBox">
      <legend className="biAuth__agreementTitle">Required agreement</legend>
      <div className="biAuth__agreementList">
        <label className="biAuth__agreementOption" htmlFor={policiesId}>
          <input
            id={policiesId}
            type="checkbox"
            required
            checked={termsAndPrivacyChecked}
            onChange={(event) => {
              const checked = event.target.checked;
              onToggle("termsOfService", checked);
              onToggle("privacyPolicy", checked);
            }}
          />
          <span>
            I agree to the{" "}
            <a
              className="biAuth__legalLink"
              href={termsPolicy?.path || "/terms-of-service"}
              target="_blank"
              rel="noreferrer"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              className="biAuth__legalLink"
              href={privacyPolicy?.path || "/privacy-policy"}
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            .
          </span>
        </label>
        <label className="biAuth__agreementOption" htmlFor={consentId}>
          <input
            id={consentId}
            type="checkbox"
            required
            checked={Boolean(selections?.sensitiveDataConsent)}
            onChange={(event) => {
              onToggle("sensitiveDataConsent", event.target.checked);
            }}
          />
          <span>
            I consent to BetterIntentions processing my sensitive information —
            my sexual orientation, religion, ethnicity, and political views — to
            provide the service, as described in the{" "}
            <a
              className="biAuth__legalLink"
              href={privacyPolicy?.path || "/privacy-policy"}
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            . These details stay private to you by default; you choose whether to
            show them on your profile.
          </span>
        </label>
      </div>
    </fieldset>
  );
}

const SignUpFormContext = React.createContext({
  agreementSelections: createDefaultAgreementSelections(),
  handleAgreementToggle: () => {},
  onSignUpCaptchaTokenChange: () => {},
  signUpCaptchaError: "",
  turnstileSignUpEnabled: false,
});

function AuthSignInFormFields() {
  return (
    <>
      <AuthPrivacyNote />
      <Authenticator.SignIn.FormFields />
    </>
  );
}

function AuthSignUpFormFields() {
  const {
    agreementSelections,
    handleAgreementToggle,
    onSignUpCaptchaTokenChange,
    signUpCaptchaError,
    turnstileSignUpEnabled,
  } = React.useContext(SignUpFormContext);

  return (
    <>
      <AuthPrivacyNote />
      <Authenticator.SignUp.FormFields />
      <SignUpAgreementFields
        selections={agreementSelections}
        onToggle={handleAgreementToggle}
      />
      {turnstileSignUpEnabled ? (
        <TurnstileSignupCaptcha
          errorMessage={signUpCaptchaError}
          onTokenChange={onSignUpCaptchaTokenChange}
        />
      ) : null}
      <AuthSignUpVerifyNote />
    </>
  );
}

// After sign-up, Cognito shows a "verify your contact information" screen where
// the email option must be SELECTED before "Verify" does anything. Users were
// missing that step, so we prime them for it here, right above the submit button.
function AuthSignUpVerifyNote() {
  return (
    <div className="biAuth__verifyNote" role="note" aria-label="Next step">
      <p className="biAuth__verifyNoteTitle">Next: verify your email</p>
      <p className="biAuth__verifyNoteBody">
        After you create your account, a verification screen appears. Select the
        email option shown, then click <strong>Verify</strong> to confirm your
        address.
      </p>
    </div>
  );
}

function AuthSignInHeader() {
  return (
    <AuthPanelHeader
      title="Welcome Back"
      subtitle="Sign in to continue your conversations and community activity."
    />
  );
}

function AuthSignUpHeader() {
  return (
    <AuthPanelHeader
      title="Create Your Account"
      subtitle="Set up your Better Intentions account to start connecting."
    />
  );
}

function EmptyAuthFooter() {
  return null;
}

// The email-code (ConfirmSignUp) screen is where new users enter the code Cognito
// emails them. Amplify only lets us override this screen's Header/Footer (not its
// FormFields), so the spam heads-up goes in the Header, above the code input,
// where the user is actively waiting for the email.
function AuthConfirmSignUpHeader() {
  return (
    <>
      <AuthPanelHeader
        title="Confirm your account"
        subtitle="Enter the code we emailed you to finish creating your account."
      />
      <div className="biAuth__verifyNote" role="note" aria-label="Don't see the email?">
        <p className="biAuth__verifyNoteTitle">Don't see the email?</p>
        <p className="biAuth__verifyNoteBody">
          The verification code sometimes lands in your spam or junk folder — check
          there if it's not in your inbox within a minute.
        </p>
      </div>
    </>
  );
}

const AUTHENTICATOR_COMPONENTS = {
  Header: AuthPanelBrandHeader,
  SignIn: {
    Header: AuthSignInHeader,
    FormFields: AuthSignInFormFields,
    Footer: AuthPanelSignInFooter,
  },
  SignUp: {
    Header: AuthSignUpHeader,
    FormFields: AuthSignUpFormFields,
    Footer: EmptyAuthFooter,
  },
  ConfirmSignUp: {
    Header: AuthConfirmSignUpHeader,
  },
};

// Cognito group claims arrive inconsistently between environments: sometimes as
// an array, sometimes as a JSON-encoded array, and sometimes as a comma-delimited
// string. Normalize all supported shapes once so staff checks do not duplicate
// parsing rules in MFA or console access code.
export function parseClaimList(rawValue) {
  if (Array.isArray(rawValue)) {
    return rawValue.map((entry) => String(entry || "").trim()).filter(Boolean);
  }

  const value = String(rawValue || "").trim();
  if (!value) return [];

  if (value.startsWith("[") && value.endsWith("]")) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map((entry) => String(entry || "").trim()).filter(Boolean);
      }
    } catch {
      // Fall back to comma parsing.
    }
  }

  return value.split(",").map((entry) => entry.trim()).filter(Boolean);
}

// Staff status is intentionally group-name based. Keep this narrow so non-staff
// authenticated users never enter the staff-only MFA setup flow by accident.
export function isStaffClaims(claims = {}) {
  const groups = parseClaimList(claims?.["cognito:groups"] ?? claims?.groups);
  return groups.some((groupName) => STAFF_GROUPS.has(String(groupName || "").trim().toLowerCase()));
}

// Amplify may return MFA type casing differently across SDK versions; comparing
// uppercase values prevents duplicate lowercase/uppercase checks.
export function normalizeMfaType(value) {
  return String(value || "").trim().toUpperCase();
}

function StaffMfaGate({ signOut, children }) {
  const signOutRef = useRef(signOut);
  const [loading, setLoading] = useState(true);
  const [policyError, setPolicyError] = useState("");
  const [setupError, setSetupError] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [setupSecret, setSetupSecret] = useState("");
  const [setupUri, setSetupUri] = useState("");
  const [setupQrDataUrl, setSetupQrDataUrl] = useState("");
  const [setupQrError, setSetupQrError] = useState("");

  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  useEffect(() => {
    let cancelled = false;

    async function enforceStaffMfa() {
      setLoading(true);
      setPolicyError("");
      setSetupError("");
      setTotpCode("");
      setSetupSecret("");
      setSetupUri("");
      setSetupQrDataUrl("");
      setSetupQrError("");

      try {
        const session = await fetchAuthSession();
        const claims = session?.tokens?.idToken?.payload || {};
        const isStaff = isStaffClaims(claims);

        if (!isStaff) {
          if (!cancelled) {
            setLoading(false);
          }
          return;
        }

        const mfaPreference = await fetchMFAPreference();
        const enabled = new Set(
          Array.isArray(mfaPreference?.enabled)
            ? mfaPreference.enabled.map((entry) => normalizeMfaType(entry)).filter(Boolean)
            : []
        );
        const preferred = normalizeMfaType(mfaPreference?.preferred);
        const hasTotpEnabled = enabled.has("TOTP");

        if (hasTotpEnabled && preferred === "TOTP") {
          if (!cancelled) {
            setLoading(false);
          }
          return;
        }

        if (hasTotpEnabled && preferred !== "TOTP") {
          await updateMFAPreference({ totp: "PREFERRED" });
          await signOutRef.current?.();
          return;
        }

        const setupDetails = await setUpTOTP();
        const secret = String(setupDetails?.sharedSecret || "").trim();
        const uri =
          typeof setupDetails?.getSetupUri === "function"
            ? String(setupDetails.getSetupUri("Better Intentions") || "")
            : "";

        if (!secret) {
          throw new Error("Unable to generate an MFA setup secret for this account.");
        }

        if (!cancelled) {
          setSetupSecret(secret);
          setSetupUri(uri);
          setLoading(false);
        }
      } catch (error) {
        if (cancelled) return;
        setLoading(false);
        setPolicyError(
          String(error?.message || "").trim() ||
            "Unable to evaluate staff MFA policy. Sign out and try signing in again."
        );
      }
    }

    enforceStaffMfa();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function generateSetupQrCode() {
      if (!setupUri) {
        setSetupQrDataUrl("");
        setSetupQrError("");
        return;
      }

      try {
        const dataUrl = await QRCode.toDataURL(setupUri, {
          width: 240,
          margin: 1,
          errorCorrectionLevel: "M",
        });
        if (!cancelled) {
          setSetupQrDataUrl(dataUrl);
          setSetupQrError("");
        }
      } catch (error) {
        console.error("Failed to generate staff MFA setup QR code:", error);
        if (!cancelled) {
          setSetupQrDataUrl("");
          setSetupQrError("Unable to render QR code. Use the setup key below for manual entry.");
        }
      }
    }

    generateSetupQrCode();
    return () => {
      cancelled = true;
    };
  }, [setupUri]);

  const onVerifyMfaSetup = async (event) => {
    event.preventDefault();
    const trimmedCode = String(totpCode || "").trim();
    if (!/^\d{6}$/.test(trimmedCode)) {
      setSetupError("Enter a valid 6-digit code from your authenticator app.");
      return;
    }

    setIsVerifying(true);
    setSetupError("");
    try {
      await verifyTOTPSetup({ code: trimmedCode });
      await updateMFAPreference({ totp: "PREFERRED" });
      await signOutRef.current?.();
    } catch (error) {
      const rawMessage = String(error?.message || "").trim();
      setSetupError(rawMessage || "Unable to verify MFA setup code. Try again.");
      setIsVerifying(false);
    }
  };

  if (loading) {
    return <div className="appLoadingState">Loading…</div>;
  }

  if (policyError) {
    return (
      <div className="appStandaloneScroll">
        <div className="opsConsole">
          <div className="opsConsole__notice opsConsole__notice--blocked">
            <h1>Couldn't check your security setup</h1>
            <p>{policyError}</p>
            <button type="button" className="biAuth__captchaRetry" onClick={() => signOut?.()}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (setupSecret) {
    return (
      <div className="appStandaloneScroll">
        <div className="opsConsole">
          <div className="opsConsole__notice opsConsole__notice--blocked">
            <h1>Set up two-factor authentication</h1>
            <p>
              Your account has staff access. Add this account to an authenticator
              app before continuing.
            </p>
            <ol className="opsConsole__mfaSteps">
              <li>
                Open an authenticator app (Google Authenticator, 1Password, Authy,
                or similar).
              </li>
              <li>Scan the QR code below — or enter the setup key by hand.</li>
              <li>Type the 6-digit code the app shows you.</li>
            </ol>
            {setupQrDataUrl ? (
              <div className="opsConsole__mfaQrPanel">
                <img
                  src={setupQrDataUrl}
                  alt="QR code"
                  className="opsConsole__mfaQrImage"
                />
              </div>
            ) : null}
            {setupQrError ? <p className="opsConsole__inlineWarn">{setupQrError}</p> : null}
            <p><strong>Setup key:</strong> <code>{setupSecret}</code></p>

            <form onSubmit={onVerifyMfaSetup}>
              <label htmlFor="staff-mfa-code">6-digit code</label>
              <input
                id="staff-mfa-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={totpCode}
                onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                disabled={isVerifying}
              />
              {setupError ? <p className="opsConsole__error">{setupError}</p> : null}
              <button type="submit" className="biAuth__captchaRetry" disabled={isVerifying}>
                {isVerifying ? "Verifying…" : "Verify"}
              </button>
            </form>
            <p className="opsConsole__mfaFootnote">
              After you verify, you'll be signed out and asked to sign in with
              the new code.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return children;
}

function RequireAuth({ children }) {
  const navigate = useNavigate();
  const [signUpCaptchaError, setSignUpCaptchaError] = useState("");
  const [isSignUpCaptchaVerified, setIsSignUpCaptchaVerified] = useState(false);
  const [agreementSelections, setAgreementSelections] = useState(() =>
    createDefaultAgreementSelections()
  );
  const agreementSelectionsRef = useRef(agreementSelections);
  const signUpCaptchaTokenRef = useRef("");
  const turnstileSignUpEnabled = useMemo(() => Boolean(resolveTurnstileSiteKey()), []);

  useEffect(() => {
    agreementSelectionsRef.current = agreementSelections;
  }, [agreementSelections]);

  const handleAgreementToggle = useCallback((policyType, checked) => {
    setAgreementSelections((current) => ({
      ...current,
      [policyType]: Boolean(checked),
    }));
  }, []);

  const onSignUpCaptchaTokenChange = useCallback((token) => {
    const normalizedToken = String(token || "").trim();
    signUpCaptchaTokenRef.current = normalizedToken;
    setIsSignUpCaptchaVerified(Boolean(normalizedToken));
    if (normalizedToken) {
      setSignUpCaptchaError("");
    }
  }, []);

  const handleSignUpWithCaptcha = useCallback(async (input) => {
    const currentAgreementSelections = agreementSelectionsRef.current;
    const nextUserAttributes = {
      ...(input?.options?.userAttributes || {}),
    };
    delete nextUserAttributes.phone_number;
    const acceptedAt = new Date().toISOString();
    const agreementMetadata = buildAgreementClientMetadata(currentAgreementSelections, acceptedAt);
    const baseSignUpInput = {
      ...input,
      options: {
        ...(input?.options || {}),
        userAttributes: nextUserAttributes,
        clientMetadata: {
          ...(input?.options?.clientMetadata || {}),
          ...agreementMetadata,
        },
        validationData: {
          ...(input?.options?.validationData || {}),
          ...agreementMetadata,
        },
      },
    };

    if (!turnstileSignUpEnabled) {
      queuePendingPolicyAcceptances({
        selections: currentAgreementSelections,
        email: input?.username || input?.options?.userAttributes?.email,
        acceptedAt,
      });
      return signUp(baseSignUpInput);
    }

    const token = String(signUpCaptchaTokenRef.current || "").trim();
    if (!token) {
      const missingTokenMessage = "Please complete CAPTCHA verification before creating an account.";
      setIsSignUpCaptchaVerified(false);
      setSignUpCaptchaError(missingTokenMessage);
      throw new Error(missingTokenMessage);
    }

    queuePendingPolicyAcceptances({
      selections: currentAgreementSelections,
      email: input?.username || input?.options?.userAttributes?.email,
      acceptedAt,
    });

    const signUpInput = {
      ...baseSignUpInput,
      options: {
        ...(baseSignUpInput?.options || {}),
        clientMetadata: {
          ...(baseSignUpInput?.options?.clientMetadata || {}),
          captchaProvider: "turnstile",
          captchaAction: TURNSTILE_SIGNUP_ACTION,
          captchaToken: token,
        },
        validationData: {
          ...(baseSignUpInput?.options?.validationData || {}),
          captchaProvider: "turnstile",
          captchaAction: TURNSTILE_SIGNUP_ACTION,
          captchaToken: token,
        },
      },
    };

    try {
      setSignUpCaptchaError("");
      return await signUp(signUpInput);
    } catch (error) {
      const rawMessage = String(error?.message || "Sign up failed.");
      const friendlyMessage =
        /captcha|turnstile|verification|bot/i.test(rawMessage)
          ? "CAPTCHA verification failed. Please retry the challenge and submit again."
          : rawMessage;

      setSignUpCaptchaError(friendlyMessage);
      signUpCaptchaTokenRef.current = "";
      setIsSignUpCaptchaVerified(false);
      throw new Error(friendlyMessage);
    }
  }, [turnstileSignUpEnabled]);

  const authenticatorServices = useMemo(
    () => ({
      handleSignUp: handleSignUpWithCaptcha,
    }),
    [handleSignUpWithCaptcha]
  );

  const authenticatorClassName = useMemo(() => {
    if (!turnstileSignUpEnabled || isSignUpCaptchaVerified) return "biAuth";
    return "biAuth biAuth--captchaPending";
  }, [isSignUpCaptchaVerified, turnstileSignUpEnabled]);

  const signUpFormContextValue = useMemo(
    () => ({
      agreementSelections,
      handleAgreementToggle,
      onSignUpCaptchaTokenChange,
      signUpCaptchaError,
      turnstileSignUpEnabled,
    }),
    [
      agreementSelections,
      handleAgreementToggle,
      onSignUpCaptchaTokenChange,
      signUpCaptchaError,
      turnstileSignUpEnabled,
    ]
  );

  return (
    <SignUpFormContext.Provider value={signUpFormContextValue}>
      <Authenticator
        className={authenticatorClassName}
        loginMechanisms={["email"]}
        signUpAttributes={["name"]}
        components={AUTHENTICATOR_COMPONENTS}
        services={authenticatorServices}
        formFields={{
          signIn: {
            username: {
              label: "Email",
              placeholder: "you@example.com",
              isRequired: true,
            },
          },
          signUp: {
            email: {
              label: "Email",
              placeholder: "you@example.com",
              isRequired: true,
            },
            name: {
              label: "Full name",
              placeholder: "Your full name",
              isRequired: true,
            },
            password: {
              label: "Password",
              isRequired: true,
            },
            confirm_password: {
              label: "Confirm password",
              isRequired: true,
            },
          },
        }}
      >
        {({ user, signOut }) => {
        const signOutToHome = async () => {
          try {
            await signOut?.();
          } finally {
            navigate("/", { replace: true });
          }
        };

        return (
          <StaffMfaGate signOut={signOutToHome}>
            {children({ user, signOut: signOutToHome })}
          </StaffMfaGate>
        );
        }}
      </Authenticator>
    </SignUpFormContext.Provider>
  );
}

function isLegalSubsectionHeading(text) {
  return /^\d+\.\d+\s+/.test(text);
}

function isOrderedLegalListItem(text) {
  return /^(?:\(?\d+\)?[.)]|\(?[a-zA-Z]\))\s+/.test(text) && !isLegalSubsectionHeading(text);
}

function stripOrderedLegalListMarker(text) {
  return String(text || "").trim().replace(/^(?:\(?\d+\)?[.)]|\(?[a-zA-Z]\))\s+/, "");
}

function isPotentialLegalListItem(text) {
  const normalizedText = String(text || "").trim();
  if (!normalizedText || normalizedText.includes(":")) return false;
  if (/[.!?]$/.test(normalizedText)) return false;
  return normalizedText.length <= 180;
}

function isLegalLabel(text) {
  const normalizedText = String(text || "").trim();
  if (!/^[A-Z][A-Za-z0-9/()\- ,&'’]+$/.test(normalizedText)) return false;
  return normalizedText.split(/\s+/).length <= 5;
}

function getLegalParagraphKind(paragraph, previousParagraph = "", previousKind = "") {
  const text = String(paragraph || "").trim();
  const previousText = String(previousParagraph || "").trim();

  if (isLegalSubsectionHeading(text)) return "subheading";
  if (/^IMPORTANT:/i.test(text)) return "important";
  if (isOrderedLegalListItem(text)) return "ordered";
  if (
    isPotentialLegalListItem(text) &&
    (previousText.endsWith(":") || previousKind === "bullet" || previousKind === "ordered")
  ) {
    return "bullet";
  }
  if (isLegalLabel(text)) return "label";
  if (isPotentialLegalListItem(text) && previousKind === "label") return "bullet";

  return "body";
}

function hasPriorLegalSubsection(paragraphs, index) {
  return paragraphs
    .slice(0, index)
    .some((previousParagraph) => getLegalParagraphKind(previousParagraph) === "subheading");
}

export function renderLegalParagraph(paragraph, index, paragraphs = [], previousKind = "") {
  const kind = getLegalParagraphKind(paragraph, paragraphs[index - 1], previousKind);
  const isSubsectionBody = hasPriorLegalSubsection(paragraphs, index);
  const contentClassName = isSubsectionBody ? " legalPage__subsectionBody" : "";

  if (kind === "subheading") {
    return (
      <h3 key={`${index}-${paragraph}`} className="legalPage__subsectionTitle">
        {paragraph}
      </h3>
    );
  }

  if (kind === "important") {
    return (
      <p key={`${index}-${paragraph}`} className={`legalPage__important${contentClassName}`}>
        {paragraph}
      </p>
    );
  }

  if (kind === "label") {
    return (
      <p key={`${index}-${paragraph}`} className={`legalPage__label${contentClassName}`}>
        {paragraph}
      </p>
    );
  }

  return (
    <p key={`${index}-${paragraph}`} className={`legalPage__body${contentClassName}`}>
      {paragraph}
    </p>
  );
}

export function renderLegalParagraphs(paragraphs = []) {
  const nodes = [];
  let index = 0;
  let previousKind = "";

  while (index < paragraphs.length) {
    const paragraph = paragraphs[index];
    const kind = getLegalParagraphKind(paragraph, paragraphs[index - 1], previousKind);

    if (kind === "bullet" || kind === "ordered") {
      const listKind = kind;
      const listItems = [];
      const listStartIndex = index;

      while (index < paragraphs.length) {
        const itemKind = getLegalParagraphKind(paragraphs[index], paragraphs[index - 1], previousKind);
        if (itemKind !== listKind) break;

        const text = listKind === "ordered"
          ? stripOrderedLegalListMarker(paragraphs[index])
          : paragraphs[index];
        listItems.push({ index, text });
        previousKind = itemKind;
        index += 1;
      }

      const isSubsectionBody = hasPriorLegalSubsection(paragraphs, listStartIndex);
      const contentClassName = isSubsectionBody ? " legalPage__subsectionBody" : "";
      const ListTag = listKind === "ordered" ? "ol" : "ul";

      nodes.push(
        <ListTag
          key={`legal-list-${listStartIndex}`}
          className={`legalPage__list legalPage__list--${listKind}${contentClassName}`}
        >
          {listItems.map((item) => (
            <li key={`${item.index}-${item.text}`} className="legalPage__listItem">
              {item.text}
            </li>
          ))}
        </ListTag>
      );
      continue;
    }

    nodes.push(renderLegalParagraph(paragraph, index, paragraphs, previousKind));
    previousKind = kind;
    index += 1;
  }

  return nodes;
}

export function LegalPage({ documentKey }) {
  const legalDocument = getLegalDocument(documentKey);
  if (!legalDocument) {
    return (
      <main className="legalPage">
        <div className="legalPage__inner">
          <h1 className="legalPage__title">Legal information unavailable</h1>
          <p className="legalPage__lead">
            The requested legal document could not be found.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="legalPage">
      <div className="legalPage__inner">
        <p className="legalPage__meta">
          Version {legalDocument.version} • Effective {legalDocument.effectiveDate}
        </p>
        <h1 className="legalPage__title">{legalDocument.title}</h1>
        <p className="legalPage__lead">{legalDocument.lead}</p>
        {legalDocument.sections.map((section) => (
          <section key={section.heading} className="legalPage__section">
            <h2 className="legalPage__sectionTitle">{section.heading}</h2>
            <div className="legalPage__sectionContent">
              {renderLegalParagraphs(section.paragraphs)}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
}

function AuthInactivityTimeout({ signOut }) {
  const signOutRef = useRef(signOut);
  const controllerRef = useRef(null);
  const [warning, setWarning] = useState({ isVisible: false, remainingMs: 0 });

  function formatCountdown(remainingMs) {
    const totalSeconds = Math.max(0, Math.ceil(Number(remainingMs || 0) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  useEffect(() => {
    let disposed = false;
    const controller = createIdleSessionController({
      timeoutMs: IDLE_TIMEOUT_MS,
      warningMs: IDLE_WARNING_MS,
      onWarningChange: ({ isVisible, remainingMs }) => {
        if (disposed) return;
        setWarning({
          isVisible,
          remainingMs: Math.max(0, Number(remainingMs) || 0),
        });
      },
      onTimeout: async () => {
        if (disposed) return;
        setWarning({ isVisible: false, remainingMs: 0 });

        try {
          await signOutRef.current?.();
        } catch (error) {
          console.error("Failed to sign out after inactivity timeout:", error);
        }
      },
    });

    controllerRef.current = controller;
    controller.start();

    return () => {
      disposed = true;
      controller.stop();
      controllerRef.current = null;
    };
  }, []);

  const onStillHere = () => {
    controllerRef.current?.reportActivity({ force: true });
  };

  if (!warning.isVisible) return null;

  return (
    <div className="idleTimeoutNotice" role="status" aria-live="polite">
      <div className="idleTimeoutNotice__copy">
        You will be logged out in{" "}
        <span className="idleTimeoutNotice__time">{formatCountdown(warning.remainingMs)}</span>{" "}
        due to inactivity.
      </div>
      <button
        type="button"
        className="idleTimeoutNotice__action"
        onClick={onStillHere}
      >
        Still here
      </button>
    </div>
  );
}

/* ----------------------------
   PUBLIC LAYOUT
----------------------------- */
export function PublicLayout() {
  return (
    <div className="appShell">
      <div id="nav-scroll-sentinel" className="appScrollSentinel" />

      <Navigation
        isAuthenticated={false}
      />

      <Outlet />
    </div>
  );
}

const KEEP_ALIVE_TOP_TAB_PATHS = new Set(["/active", "/connect", "/past"]);

// Keep-alive tabs only match exact normalized paths. This trims accidental
// trailing slashes without collapsing nested chat routes into their parent tabs.
export function normalizeRoutePath(pathname) {
  const rawPath = String(pathname || "").trim();
  if (!rawPath) return "/";
  if (rawPath.length > 1) {
    return rawPath.replace(/\/+$/, "") || "/";
  }
  return rawPath;
}

function KeepAliveTopTabs({ activePath }) {
  const showActive = activePath === "/active";
  const showConnect = activePath === "/connect";
  const showPast = activePath === "/past";

  return (
    <>
      <div
        style={{ display: showActive ? "block" : "none" }}
        aria-hidden={!showActive}
      >
        <Active />
      </div>
      <div
        style={{ display: showConnect ? "block" : "none" }}
        aria-hidden={!showConnect}
      >
        <CommunityHub />
      </div>
      <div
        style={{ display: showPast ? "block" : "none" }}
        aria-hidden={!showPast}
      >
        <Past />
      </div>
    </>
  );
}


/* ----------------------------
   AUTHENTICATED TOP NAV LAYOUT
----------------------------- */
function TopNavShell({ userEmail, userName, userAvatarUrl, userAvatarColor, signOut, currentUser }) {
  const location = useLocation();
  const [hasActiveNotifications, setHasActiveNotifications] = useState(false);
  const [hasPingNotifications, setHasPingNotifications] = useState(false);
  const communicationSnapshotsRef = useRef({
    active: null,
    ping: null,
    past: null,
  });
  const currentPathRef = useRef(location.pathname);
  const isCommunityRequestsViewRef = useRef(
    location.pathname === "/connect" &&
      new URLSearchParams(location.search).get("view") === "requests"
  );
  const isCommunityRequestsView =
    location.pathname === "/connect" &&
    new URLSearchParams(location.search).get("view") === "requests";
  const normalizedPathname = normalizeRoutePath(location.pathname);
  const isKeepAliveTopTabRoute = KEEP_ALIVE_TOP_TAB_PATHS.has(normalizedPathname);

  useEffect(() => {
    currentPathRef.current = location.pathname;
    isCommunityRequestsViewRef.current = isCommunityRequestsView;
  }, [isCommunityRequestsView, location.pathname]);

  useEffect(() => {
    let cancelled = false;
    let stopHeartbeat = () => {};

    async function initPresence() {
      try {
        const user = await getCurrentUser();
        if (!user?.userId || cancelled) return;
        stopHeartbeat = startPresenceHeartbeat({ userId: user.userId });
      } catch (error) {
        console.error("Failed to initialize presence heartbeat:", error);
      }
    }

    initPresence();
    return () => {
      cancelled = true;
      stopHeartbeat();
    };
  }, []);

  const getCommunicationSnapshot = useCallback((surface, userId) => {
    const key = String(surface || "").trim();
    const normalizedUserId = String(userId || "").trim();
    if (!key || !normalizedUserId) return null;
    const entry = communicationSnapshotsRef.current[key];
    if (!entry || entry.userId !== normalizedUserId) return null;
    return {
      ...entry,
      payload: entry.payload ?? null,
    };
  }, []);

  const setCommunicationSnapshot = useCallback((surface, userId, payload) => {
    const key = String(surface || "").trim();
    const normalizedUserId = String(userId || "").trim();
    if (!key || !normalizedUserId) return;
    communicationSnapshotsRef.current[key] = {
      userId: normalizedUserId,
      updatedAt: new Date().toISOString(),
      payload: payload ?? null,
    };
  }, []);

  const communicationCache = useMemo(
    () => ({
      getSnapshot: getCommunicationSnapshot,
      setSnapshot: setCommunicationSnapshot,
    }),
    [getCommunicationSnapshot, setCommunicationSnapshot]
  );

  const outletContextValue = useMemo(
    () => ({
      setHasActiveNotifications,
      setHasPingNotifications,
      signOut,
      communicationCache,
      currentUser: {
        userId: String(currentUser?.userId || "").trim() || null,
        email: currentUser?.email || null,
        name: currentUser?.name || null,
      },
    }),
    [
      communicationCache,
      currentUser?.email,
      currentUser?.name,
      currentUser?.userId,
      setHasActiveNotifications,
      setHasPingNotifications,
      signOut,
    ]
  );

  async function writeMonotonicLastViewed(userId, field, candidateIso) {
    try {
      const userResp = await client.models.User.get({ userId });
      const existing = userResp?.data?.[field] || "";
      const nextValue = getNextMonotonicIso(existing, candidateIso);

      if (userResp.data) {
        await client.models.User.update({
          userId,
          [field]: nextValue,
        });
      } else {
        await client.models.User.create({
          userId,
          [field]: nextValue,
        });
      }
    } catch (e) {
      console.error(`Failed to update ${field}:`, e);
    }
  }

  // Load notification status on mount using Amplify
  useEffect(() => {
    let cancelled = false;

    async function loadNotifications() {
      try {
        const user = await getCurrentUser();
        if (!user?.userId || cancelled) return;

        const mySenderId = user.userId;
        const shouldCheckPing = !isCommunityRequestsView;
        const snapshot = await loadCommunicationInboxSnapshot({
          userId: mySenderId,
          maxAgeMs: 4_000,
        });
        if (cancelled) return;

        const latestSyncedAt = String(snapshot?.latestSyncedAt || new Date().toISOString());
        const activeChats = Array.isArray(snapshot?.activeChats) ? snapshot.activeChats : [];
        const pingReceivedRequests = Array.isArray(snapshot?.pingReceivedRequests)
          ? snapshot.pingReceivedRequests
          : [];
        const pingAcceptedRequests = Array.isArray(snapshot?.pingAcceptedRequests)
          ? snapshot.pingAcceptedRequests
          : [];
        const pastChats = Array.isArray(snapshot?.pastChats) ? snapshot.pastChats : [];

        setCommunicationSnapshot("active", mySenderId, {
          availableChats: activeChats,
          pendingRequests: [],
          latestSyncedAt,
        });
        setCommunicationSnapshot("ping", mySenderId, {
          receivedRequests: pingReceivedRequests,
          acceptedRequests: pingAcceptedRequests,
          latestSyncedAt,
        });
        setCommunicationSnapshot("past", mySenderId, {
          availableChats: pastChats,
          latestSyncedAt,
        });

        const hasNewMessages = activeChats.some((chat) => {
          const isPendingIncoming = chat?.status === "pending" && !chat?.isInitiatedByMe;
          return isPendingIncoming || Number(chat?.unreadCount || 0) > 0;
        });
        setHasActiveNotifications(hasNewMessages);

        if (!shouldCheckPing) {
          setHasPingNotifications(false);
          return;
        }

        let lastSeenPingTimestamp = "";
        try {
          const userResp = await client.models.User.get({ userId: mySenderId });
          lastSeenPingTimestamp = String(userResp?.data?.last_viewed_ping || "");
        } catch (error) {
          console.error("Failed to fetch user last viewed ping timestamp:", error);
        }

        const pingRows = [...pingReceivedRequests, ...pingAcceptedRequests];
        const hasNewRequests = pingRows.some(
          (row) => String(row?.lastUpdatedAt || "") > lastSeenPingTimestamp
        );
        setHasPingNotifications(hasNewRequests);

      } catch (err) {
        console.error("Failed to load notifications:", err);
      }
    }

    loadNotifications();

    return () => {
      cancelled = true;
    };
  }, [isCommunityRequestsView, setCommunicationSnapshot]);

  // Update last viewed timestamp when user navigates to tabs
  useEffect(() => {
    async function updateLastViewed() {
      try {
        const user = await getCurrentUser();
        if (!user?.userId) return;

        const mySenderId = user.userId;
        const currentPath = location.pathname;

        // Determine which timestamp to update based on current path
        let updateField = null;
        let updateValue = null;

        if (currentPath === "/ping" || isCommunityRequestsView) {
          // Update last_viewed_ping with current timestamp
          updateField = "last_viewed_ping";
          updateValue = new Date().toISOString();
          setHasPingNotifications(false);
        }

        if (updateField && updateValue) {
          await writeMonotonicLastViewed(mySenderId, updateField, updateValue);
        }
      } catch (err) {
        console.error("Failed to update last viewed:", err);
      }
    }

    updateLastViewed();
  }, [isCommunityRequestsView, location.pathname, location.search]);

  // Subscribe to user-scoped real-time inbox events.
  useEffect(() => {
    let cancelled = false;
    let channel = null;
    let subscription = null;
    let reconnectTimer = null;
    let reconnectAttempt = 0;
    const RECONNECT_BASE_MS = 1_000;
    const RECONNECT_MAX_MS = 30_000;

    // Diagnostic logs only in non-production builds. In production we
    // surface failures via console.error / structured metrics, not
    // per-event verbose logs.
    const debugRealtime = (...args) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.log(...args);
      }
    };

    function scheduleReconnect() {
      if (cancelled) return;
      const delay = Math.min(
        RECONNECT_BASE_MS * 2 ** reconnectAttempt,
        RECONNECT_MAX_MS
      );
      reconnectAttempt += 1;
      debugRealtime(`[realtime] reconnecting in ${delay}ms (attempt ${reconnectAttempt})`);
      reconnectTimer = setTimeout(() => {
        if (!cancelled) {
          setupUserChannelSubscription();
        }
      }, delay);
    }

    async function setupUserChannelSubscription() {
      // Tear down any previous connection before re-entering.
      try { subscription?.unsubscribe?.(); } catch {}
      try { channel?.close?.(); } catch {}
      subscription = null;
      channel = null;

      try {
        const user = await getCurrentUser();
        if (!user?.userId || cancelled) return;

        // Fetch the user's random subscribe_channel_id from their
        // profile and subscribe to /default/user/<channel_id>. Routing
        // realtime through this random id (instead of the cognitoId)
        // closes the IDOR where another signed-in user could subscribe
        // to the victim's channel just by knowing their cognitoId
        // (which searchUsers exposes for discovery). The backend lambda
        // lazy-backfills the field on first read so existing accounts
        // get one transparently.
        const profileResp = await client.queries.getUserProfile({
          cognitoId: user.userId,
        });
        if (cancelled) return;
        const subscribeChannelId = String(
          profileResp?.data?.subscribe_channel_id || ""
        ).trim();
        // Diagnostic: confirm we have a channel id and what it is. Pull
        // these logs (single line, copy-paste safe) when realtime
        // doesn't arrive — they tell us whether the FE is subscribed at
        // all and to which channel.
        debugRealtime(
          "[realtime] user profile fetched",
          {
            hasProfile: Boolean(profileResp?.data),
            hasChannelId: Boolean(subscribeChannelId),
            channelIdPreview: subscribeChannelId
              ? `${subscribeChannelId.slice(0, 8)}…`
              : null,
            profileErrors: profileResp?.errors || null,
          }
        );
        if (!subscribeChannelId) {
          console.error(
            "user channel subscribe skipped: missing subscribe_channel_id on profile"
          );
          return;
        }

        const { events } = await import("aws-amplify/data");
        const channelPath = `default/user/${subscribeChannelId}`;
        debugRealtime("[realtime] connecting to channel", channelPath);
        channel = await events.connect(channelPath);

        if (cancelled) {
          try {
            channel?.close?.();
          } catch {}
          return;
        }

        debugRealtime("[realtime] subscribing to channel");
        subscription = channel.subscribe({
          next: (data) => {
            // Successful event resets the backoff counter.
            reconnectAttempt = 0;
            debugRealtime("[realtime] event received", {
              hasEvent: Boolean(data?.event),
              keys: data ? Object.keys(data) : [],
            });
            const rawPayload = extractUserChannelPayload(data);
            const payload = publishCommunicationEvent(rawPayload);
            if (!payload?.type) return;

            const currentPath = String(currentPathRef.current || "");
            const isPingSurfaceOpen =
              currentPath === "/ping" || Boolean(isCommunityRequestsViewRef.current);

            if (payload.type === "chat_message") {
              if (payload.messageFrom === user.userId) return;
              const isSameOpenActiveChat =
                currentPath.startsWith("/active/chat/") &&
                payload.chatPk &&
                payload.chatPk === getCurrentOpenChatPk();
              if (!isSameOpenActiveChat) {
                setHasActiveNotifications(true);
              }
              return;
            }

            if (payload.type === "chat_request_created") {
              if (payload.initiatedBy === user.userId) return;
              if (!isPingSurfaceOpen) {
                setHasPingNotifications(true);
              }
              return;
            }

            if (payload.type === "chat_request_updated") {
              if (payload.actedBy === user.userId) return;
              if (!isPingSurfaceOpen) {
                setHasPingNotifications(true);
              }
              return;
            }

            if (payload.type === "chat_deleted") {
              if (payload.actedBy === user.userId) return;
              if (!isPingSurfaceOpen) {
                setHasPingNotifications(true);
              }
            }
          },
          error: (err) => {
            console.error("user channel subscribe error:", err);
            scheduleReconnect();
          }
        });
      } catch (err) {
        console.error("Failed to setup user channel subscription:", err);
        scheduleReconnect();
      }
    }

    setupUserChannelSubscription();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try {
        subscription?.unsubscribe?.();
      } catch {}
      try {
        channel?.close?.();
      } catch {}
    };
  }, []);

  const isTopNavArea =
    location.pathname === "/active" ||
    location.pathname === "/past" ||
    location.pathname.startsWith("/past/") ||
    location.pathname === "/ping" ||
    location.pathname === "/connect" ||
    location.pathname.startsWith("/connect/");
    const isConsoleRoute =
    location.pathname === "/console" || location.pathname.startsWith("/console/");

  // Chat routes should be fullscreen without navigation
  const isChatRoute =
    location.pathname.startsWith("/active/chat/") ||
    location.pathname.startsWith("/past/chat/");

  const shellContent = isChatRoute ? (
    <div className="appShell appShell--full">
      <Outlet context={outletContextValue} />
    </div>
  ) : isTopNavArea ? (
    <div className="appShell">
      <Navigation
        isAuthenticated
        userEmail={userEmail}
        userName={userName}
        userAvatarUrl={userAvatarUrl}
        userAvatarColor={userAvatarColor}
        hidePrimaryTabs={isConsoleRoute}
        onSignOut={signOut}
        hasActiveNotifications={hasActiveNotifications}
        hasPingNotifications={hasPingNotifications}
      />

      <div className="appShell__scrollLayer">
        {/* 👇 SENTINEL MUST BE FIRST INSIDE SCROLLER */}
        <div id="nav-scroll-sentinel" className="appScrollSentinel" />

        {isKeepAliveTopTabRoute ? (
          <KeepAliveTopTabs activePath={normalizedPathname} />
        ) : (
          <Outlet context={outletContextValue} />
        )}
      </div>
    </div>
  ) : (
    <div className="appShell">
      <Navigation
        isAuthenticated
        userEmail={userEmail}
        userName={userName}
        userAvatarUrl={userAvatarUrl}
        userAvatarColor={userAvatarColor}
        hidePrimaryTabs={isConsoleRoute}
        onSignOut={signOut}
        hasActiveNotifications={hasActiveNotifications}
        hasPingNotifications={hasPingNotifications}
      />

      {/* 👇 Sentinel for window-based scrolling */}
      <div id="nav-scroll-sentinel" className="appScrollSentinel" />

      <div className="appShell__contentFill">
        <Outlet context={outletContextValue} />
      </div>
    </div>
  );

  return (
    <ActiveProvider outletContextOverride={outletContextValue}>
      <PastProvider outletContextOverride={outletContextValue}>
        <PingActivityProvider outletContextOverride={outletContextValue}>
          {shellContent}
        </PingActivityProvider>
      </PastProvider>
    </ActiveProvider>
  );

}

/* ----------------------------
   PROFILE STATE (SHARED)
----------------------------- */
function useProfileState({ user }) {
  const [me, setMe] = useState({
    userId: null,
    email: null,
    loading: true,
  });
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userAvatarUrl, setUserAvatarUrl] = useState("");
  const authUsername = String(user?.username || "").trim();
  const profileContextRef = useRef("");
  const hasHydratedProfileRef = useRef(false);

  // Create only once per userId (helps with StrictMode)
  const createAttemptedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function loadMe() {
      try {
        const u = await getCurrentUser();
        const attrs = await fetchUserAttributes();
        if (cancelled) return;
        setMe({
          userId: u?.userId ?? null,
          email: attrs?.email ?? null,
          loading: false,
        });
      } catch {
        if (!cancelled) {
          setMe({
            userId: null,
            email: null,
            loading: false,
          });
        }
      }
    }

    loadMe();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reset create-once guard when user changes
  useEffect(() => {
    createAttemptedRef.current = false;
  }, [me.userId]);

  // Records the browser's "do not sell or share" opt-out (CPRA
  // §1798.135(e)): an active GPC signal is stored locally (covers anonymous
  // visitors and survives the signal being turned off later), and any
  // signal or stored opt-out is fire-and-forgotten onto the authenticated
  // user's profile so it applies account-wide. Failures only warn — the
  // local record stands and the sync retries on next load.
  const recordPrivacyOptOut = useCallback((profile, cognitoId) => {
    if (isGpcSignalActive()) {
      setStoredOptOut(true);
    }
    syncPrivacyOptOutToProfile({
      cognitoId,
      profile,
      setPrivacyOptOutMutation: client?.mutations?.setPrivacyOptOut,
    }).catch((error) => {
      console.warn("Failed to record privacy opt-out on profile:", error);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const profileContextKey = `${String(me.userId || "")}::${authUsername}`;
    if (profileContextRef.current !== profileContextKey) {
      profileContextRef.current = profileContextKey;
      hasHydratedProfileRef.current = false;
    }

    async function initProfile() {
      if (me.loading) return;

      if (!hasHydratedProfileRef.current) {
        setLoading(true);
      }

        if (!me.userId) {
          setProfileComplete(false);
          setUserData(null);
        setUserAvatarUrl("");
        hasHydratedProfileRef.current = true;
        setLoading(false);
        recordPrivacyOptOut(null, "");
        return;
      }

      const userEmail = me.email || authUsername || "";
      const fallbackName = authUsername || userEmail || "User";

      try {
        const res = await client.queries.getUserProfile({
          cognitoId: me.userId,
        });
        if (cancelled) return;

        if (res?.data) {
          const data = res.data;
          setUserData(data);
          setProfileComplete(Boolean(data?.isProfileComplete));
          clearPendingPolicyAcceptancesForEmail({ userEmail: me.email });
          recordPrivacyOptOut(data, me.userId);

          if (data?.profilePicture) {
            try {
              const resolvedUrl = await resolveProfileImageUrlFromValue(data.profilePicture);
              setUserAvatarUrl(resolvedUrl || "");
            } catch (err) {
              console.error("Error fetching avatar URL:", err);
              setUserAvatarUrl("");
            }
          } else {
            setUserAvatarUrl("");
          }
          return;
        }

        // Not found -> create once
        if (!createAttemptedRef.current) {
          createAttemptedRef.current = true;
          const pendingPolicyAcceptances = getPendingPolicyAcceptanceInputs({
            userEmail: me.email,
          });

          const createRes = await client.mutations.createUserProfile({
            cognitoId: me.userId,
            email: me.email,
            name: fallbackName,
            policyAcceptances: pendingPolicyAcceptances,
          });

          if (cancelled) return;

          if (createRes?.data) {
            const created = createRes.data;
            setUserData(created);
            setProfileComplete(Boolean(created?.isProfileComplete)); // likely false
            setUserAvatarUrl("");
            clearPendingPolicyAcceptancesForEmail({ userEmail: me.email });
            recordPrivacyOptOut(created, me.userId);
            return;
          }
        }

        setProfileComplete(false);
        setUserAvatarUrl("");
      } catch (e) {
        console.error("Error initializing profile:", e);
        setProfileComplete(false);
        setUserAvatarUrl("");
      } finally {
        if (!cancelled) {
          hasHydratedProfileRef.current = true;
          setLoading(false);
        }
      }
    }

    initProfile();

    // Listen for profile updates
    const unsubscribe = Hub.listen('profile', (data) => {
      if (data.payload.event === 'profile-updated') {
        initProfile();
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [me.userId, me.loading, me.email, authUsername, recordPrivacyOptOut]);

  const userEmail = me.email || authUsername || "";
  const userName = userData?.profileName || authUsername || userEmail || "User";
  const userAvatarColor = pickChatColor(userData);

  return {
    loading,
    profileComplete,
    userEmail,
    userName,
    userAvatarUrl,
    userAvatarColor,
    me,
  };
}

/* ----------------------------
   LAYOUT 1: PROFILE INCOMPLETE (NO NAV)
----------------------------- */
function IncompleteProfileLayout({ user }) {
  const location = useLocation();
  const { loading, profileComplete } = useProfileState({ user });

  if (loading) return <div className="appLoadingState">Loading…</div>;

  // If complete, kick them to /active
  if (profileComplete) {
    return <Navigate to="/active" replace />;
  }

  // If incomplete, force ONLY /profile
  if (location.pathname !== "/profile") {
    return <Navigate to="/profile" replace />;
  }

  // Fullscreen, no nav
  return (
    <div className="appStandaloneScroll">
      <Outlet />
    </div>
  );
}

/* ----------------------------
   LAYOUT 2: PROFILE COMPLETE (SHOW NAV)
----------------------------- */
function AuthedLayout({ user, signOut }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [accessCheckDone, setAccessCheckDone] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [walkthroughStepIndex, setWalkthroughStepIndex] = useState(0);
  const hasHydratedShellRef = useRef(false);
  const {
    loading,
    profileComplete,
    userEmail,
    userName,
    userAvatarUrl,
    userAvatarColor,
    me,
  } = useProfileState({ user });

  useEffect(() => {
    if (!loading && accessCheckDone) {
      hasHydratedShellRef.current = true;
    }
  }, [accessCheckDone, loading]);

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    async function enforceAccessState() {
      try {
        const state = await getMyAccessState();
        const status = String(state?.status || "ACTIVE").toUpperCase();
        const disabledUntilMs = state?.disabledUntil ? new Date(state.disabledUntil).getTime() : null;
        const isSuspended = status === "SUSPENDED" && (!disabledUntilMs || disabledUntilMs > Date.now());

        if ((status === "DISABLED" || isSuspended) && !cancelled) {
          await signOut?.();
          return;
        }

      } catch (error) {
        const message = String(error?.message || "");
        const status = Number(error?.response?.status || error?.statusCode || 0);
        // Fail closed on clear auth/authz failures: explicit ACCOUNT_DISABLED
        // messages, HTTP 401/403, or token-expiry signals. Only treat genuine
        // transient errors (network timeouts, 5xx) as "stay signed in, retry".
        const isAuthFailure =
          message.includes("ACCOUNT_DISABLED") ||
          message.toLowerCase().includes("unauthorized") ||
          message.toLowerCase().includes("not authorized") ||
          message.toLowerCase().includes("token") ||
          status === 401 ||
          status === 403;
        if (isAuthFailure && !cancelled) {
          await signOut?.();
          return;
        }
        console.error("Failed to evaluate access state:", error);
      } finally {
        if (!cancelled) {
          setAccessCheckDone(true);
        }
      }
    }

    enforceAccessState();
    intervalId = setInterval(enforceAccessState, 60_000);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [signOut]);

  // If user came from guest browse with a pending recipient, route to connect to continue.
  useEffect(() => {
    if (loading || !accessCheckDone || !profileComplete) return;
    const pendingMessageIntent = readPendingMessageIntent();
    if (!pendingMessageIntent?.recipientId) return;
    if (location.pathname === "/connect") return;
    navigate("/connect", { replace: true });
  }, [accessCheckDone, loading, location.pathname, navigate, profileComplete]);

  useEffect(() => {
    let cancelled = false;

    async function evaluateWalkthroughState() {
      if (loading || !accessCheckDone || !profileComplete) return;

      try {
        const currentUser = await getCurrentUser();
        if (!currentUser?.userId || cancelled) return;

        const walkthroughCompleted = await getWalkthroughCompletion({
          client,
          userId: currentUser.userId,
        });

        if (cancelled) return;

        if (walkthroughCompleted) {
          setShowWalkthrough(false);
          return;
        }

        const hasRequest = await hasInitiatedChatRequest({
          client,
          userId: currentUser.userId,
        });

        if (cancelled) return;

        if (hasRequest) {
          await markWalkthroughCompleted({ client, userId: currentUser.userId });
          setShowWalkthrough(false);
        } else {
          setShowWalkthrough(true);
          setWalkthroughStepIndex(0);
        }
      } catch (error) {
        console.error("Failed to evaluate walkthrough state:", error);
      }
    }

    evaluateWalkthroughState();

    return () => {
      cancelled = true;
    };
  }, [accessCheckDone, loading, profileComplete]);

  useEffect(() => {
    const handleCompleted = () => setShowWalkthrough(false);
    window.addEventListener("walkthrough:completed", handleCompleted);
    return () => window.removeEventListener("walkthrough:completed", handleCompleted);
  }, []);

  useEffect(() => {
    if (!showWalkthrough) return;

    const activeStep = WALKTHROUGH_STEPS[walkthroughStepIndex];
    if (activeStep?.id !== "nav-connect") return;
    if (!location.pathname.startsWith(activeStep.advanceOnPathPrefix || "")) return;

    setWalkthroughStepIndex((currentIndex) => {
      if (currentIndex !== walkthroughStepIndex) return currentIndex;
      return Math.min(currentIndex + 1, WALKTHROUGH_STEPS.length - 1);
    });
  }, [location.pathname, showWalkthrough, walkthroughStepIndex]);


  useEffect(() => {
    const stepId = showWalkthrough ? WALKTHROUGH_STEPS[walkthroughStepIndex]?.id : null;
    try {
      window.dispatchEvent(
        new CustomEvent("walkthrough:step", { detail: { stepId } })
      );
    } catch (error) {
      // Ignore if CustomEvent is unavailable.
    }
  }, [showWalkthrough, walkthroughStepIndex]);

  useEffect(() => {
    try {
      window.dispatchEvent(
        new CustomEvent("walkthrough:state", { detail: { active: showWalkthrough } })
      );
    } catch (error) {
      // Ignore if CustomEvent is unavailable.
    }
  }, [showWalkthrough]);

  const dismissWalkthrough = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      if (currentUser?.userId) {
        await markWalkthroughCompleted({ client, userId: currentUser.userId });
      }
      try {
        window.dispatchEvent(new CustomEvent("walkthrough:completed"));
      } catch (error) {
        // Ignore if CustomEvent is unavailable.
      }
    } finally {
      setShowWalkthrough(false);
    }
  }, []);

  useEffect(() => {
    if (!showWalkthrough) return;

    const activeStep = WALKTHROUGH_STEPS[walkthroughStepIndex];
    if (!activeStep?.route) return;
    if (
      activeStep.advanceOnPathPrefix &&
      location.pathname.startsWith(activeStep.advanceOnPathPrefix)
    ) {
      return;
    }
    if (location.pathname.startsWith(activeStep.route)) return;

    navigate(activeStep.route, { replace: true });
  }, [location.pathname, navigate, showWalkthrough, walkthroughStepIndex]);

  useEffect(() => {
    if (!showWalkthrough) return undefined;

    const advanceToStep = (stepId) => {
      const nextIndex = WALKTHROUGH_STEPS.findIndex((step) => step.id === stepId);
      if (nextIndex < 0 || nextIndex === walkthroughStepIndex) return;

      const nextStep = WALKTHROUGH_STEPS[nextIndex];
      if (nextStep?.route && !location.pathname.startsWith(nextStep.route)) {
        navigate(nextStep.route);
      }
      setWalkthroughStepIndex(nextIndex);
    };

    const handleWalkthroughAction = async (event) => {
      const action = String(event?.detail?.action || "").trim();
      const activeStepId = WALKTHROUGH_STEPS[walkthroughStepIndex]?.id;

      if (action === "filters-loaded" && activeStepId === "filters-overview") {
        advanceToStep("results-overview");
        return;
      }

      if (action === "composer-opened" && activeStepId === "send-request") {
        advanceToStep("request-message");
        return;
      }

      if (action === "request-sent") {
        await dismissWalkthrough();
      }
    };

    window.addEventListener("walkthrough:action", handleWalkthroughAction);
    return () => window.removeEventListener("walkthrough:action", handleWalkthroughAction);
  }, [dismissWalkthrough, location.pathname, navigate, showWalkthrough, walkthroughStepIndex]);

  const shouldShowBlockingShellLoader =
    (loading || !accessCheckDone) &&
    !hasHydratedShellRef.current;

  if (shouldShowBlockingShellLoader) {
    return <div className="appLoadingState">Loading…</div>;
  }

  // If not complete, kick them back to /profile (and since this layout shows nav,
  // we do not want them here)
  if (!profileComplete) {
    return <Navigate to="/profile" replace />;
  }

  const activeStep = WALKTHROUGH_STEPS[walkthroughStepIndex];
  const shouldShowOverlay =
    showWalkthrough && activeStep && location.pathname.startsWith(activeStep.route);

  return (
    <>
      <TopNavShell
        key={me?.userId || ""}
        userEmail={userEmail}
        userName={userName}
        userAvatarUrl={userAvatarUrl}
        userAvatarColor={userAvatarColor}
        signOut={signOut}
        currentUser={{
          userId: me?.userId || null,
          email: userEmail,
          name: userName,
        }}
      />
      <WalkthroughOverlay
        isOpen={Boolean(shouldShowOverlay)}
        step={activeStep}
        stepIndex={walkthroughStepIndex}
        totalSteps={WALKTHROUGH_STEPS.length}
        onSkip={dismissWalkthrough}
        onNext={() => {
          if (walkthroughStepIndex >= WALKTHROUGH_STEPS.length - 1) {
            dismissWalkthrough();
            return;
          }
          const nextIndex = Math.min(
            walkthroughStepIndex + 1,
            WALKTHROUGH_STEPS.length - 1
          );
          const nextStep = WALKTHROUGH_STEPS[nextIndex];
          if (nextStep?.route && location.pathname !== nextStep.route) {
            navigate(nextStep.route);
          }
          setWalkthroughStepIndex(nextIndex);
        }}
      />
    </>
  );
}



/* ----------------------------
   AUTHED ROUTE BRIDGES
----------------------------- */
export function ProtectedAuthLayout() {
  // One Authenticator boundary is enough for every protected route. Child
  // layouts receive the authenticated user/signOut pair through Outlet context,
  // which avoids the previous pattern of nesting RequireAuth multiple times.
  return (
    <RequireAuth>
      {({ user, signOut }) => (
        <>
          <AuthInactivityTimeout signOut={signOut} />
          <Outlet context={{ user, signOut }} />
        </>
      )}
    </RequireAuth>
  );
}

export function IncompleteProfileRoute() {
  // Profile setup should be full-screen and nav-free until completion.
  const { user } = useOutletContext() || {};
  return <IncompleteProfileLayout user={user} />;
}

export function AuthedRoute() {
  // Completed profiles use the standard authenticated shell with nav, keep-alive
  // top tabs, notifications, and walkthrough support.
  const { user, signOut } = useOutletContext() || {};
  return <AuthedLayout user={user} signOut={signOut} />;
}
